"use client"

import { useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import Link from "next/link"
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from "motion/react"
import { EASE_OUT_EXPO, FADE_TRANSITION } from "@/lib/motion-presets"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { CheckCircle, Stethoscope, Leaf, RefreshCw, WifiOff, Loader2 } from "lucide-react"
import { isAppPromoLive } from "@/components/ui/app-download-cta"
import { analytics, trackEvent, withResultCtaLocation } from "@/lib/analytics"
import { ANONYMOUS_WEB_ANALYSIS_LIMITS, REGISTERED_FREE_ANALYSIS_LIMITS } from "@/lib/auth/feature-flags"

import { useHomepageAnalysis } from "@/hooks/use-homepage-analysis"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useAnalysisLawnContext } from "@/hooks/use-analysis-lawn-context"
import { useLastAnalysisBreadcrumb } from "@/hooks/use-last-analysis-breadcrumb"
import { useWeedIdCapability } from "@/hooks/use-weed-id-capability"
import { scrollRefinementTargetIntoView } from "@/lib/navigation/scroll-refinement-target"
import { LastAnalysisBanner } from "@/components/home/last-analysis-banner"
import { AnonLimitWall, AnonQuotaNotice, PhotoCapGate, PremiumLimitWall } from "@/components/home/limit-walls"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>
// Static — these render at initial paint
import {
  HeroSection,
  UploadCard,
  HomeHeroShell,
  HeroSocialProof,
  WizardProgress,
  FinalCta,
  MidPageCta,
  PricingPreview,
  CarePlanCardSvg,
  HeroGrassThrough,
  type WizardStep,
} from "@/components/home"

function LazyFlowFallback({ label, compact = false }: { label: string; compact?: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`mx-auto flex w-full max-w-2xl items-center justify-center gap-3 rounded-2xl border border-green-100 bg-white/75 px-5 text-sm font-medium text-green-900 shadow-sm ${compact ? 'min-h-24' : 'min-h-40'}`}
    >
      <Loader2 className="h-5 w-5 animate-spin text-green-600" aria-hidden="true" />
      <span>{label}</span>
    </div>
  )
}

// Dynamic imports use leaf paths (not the @/components/home barrel) to
// guarantee each lazy component lands in its own chunk. Going through the
// barrel collapses lazy children into a shared chunk that's already loaded
// by the below-the-fold sections — defeating the point of deferring them.


// HowItWorksIllustrated is a Client Component passed from app/page.tsx via the
// `aboveCalculatorsSlot` prop. Because this LandingContent suspends on
// useSearchParams and `/` is statically prerendered, that slot sits behind the
// Suspense fallback, so its prose is NOT in the static HTML — crawlers see it
// only after running JS. The crawl-safe signal is the HowTo JSON-LD emitted
// outside the Suspense boundary in app/page.tsx. ToolsChipStrip stays
// dynamic-client (still uses motion entrance animations).
const ToolsChipStrip = dynamic(
  () => import('@/components/home/tools-chip-strip').then((mod) => ({ default: mod.ToolsChipStrip })),
  { loading: () => <LazyFlowFallback label="Loading lawn tools..." compact /> }
)

// iPhone-app bridge section — below the fold and self-gated on the app-promo
// launch env vars, so it stays out of the initial homepage chunk.
const HomeAppSection = dynamic(
  () => import('@/components/home/home-app-section').then((mod) => ({ default: mod.HomeAppSection })),
  { loading: () => <LazyFlowFallback label="Loading app details..." compact /> }
)

// Post-interaction components — only render after the user uploads, runs an
// analysis, or hits the limit-reached path. Each gets its own chunk so they
// don't bloat the initial homepage payload. The prefetch effect below warms
// the analyze + result chunks at upload time so they're cached before the
// state transitions that mount them — avoids a blank-card flash on click.
const ZipCodeStep = dynamic(
  () => import('@/components/home/zip-code-step').then((mod) => ({ default: mod.ZipCodeStep })),
  { loading: () => <LazyFlowFallback label="Loading the location step..." /> }
)
const AnalysisLoading = dynamic(
  () => import('@/components/home/analysis-loading').then((mod) => ({ default: mod.AnalysisLoading })),
  { loading: () => <LazyFlowFallback label="Preparing your lawn analysis..." /> }
)
const ResultsDisplay = dynamic(
  () => import('@/components/home/results-display').then((mod) => ({ default: mod.ResultsDisplay })),
  { loading: () => <LazyFlowFallback label="Loading your grass result..." /> }
)
const DiagnosisResults = dynamic(
  () => import('@/components/home/diagnosis-results').then((mod) => ({ default: mod.DiagnosisResults })),
  { loading: () => <LazyFlowFallback label="Loading your lawn diagnosis..." /> }
)
const WeedResultPresentation = dynamic(
  () => import('@/components/weed-identifier/weed-result-presentation').then((mod) => ({ default: mod.WeedResultPresentation })),
  { loading: () => <LazyFlowFallback label="Loading your weed result..." /> }
)

export function LawnCareLandingContent({
  aboveCalculatorsSlot,
  socialProofCountLabel,
}: {
  aboveCalculatorsSlot?: React.ReactNode
  /** Live "N+ lawns identified" label computed server-side in app/page.tsx.
   * Omitted when the verified count is unavailable. */
  socialProofCountLabel?: string | null
} = {}) {
  const { lawns } = useDefaultLawn()
  const analysisLawn = useAnalysisLawnContext(lawns)
  const { capability: weedCapability } = useWeedIdCapability()
  const {
    // Image state
    uploadedImage,
    uploadedImageUrl,
    isUploading,
    uploadError,
    supportingImages,
    isUploadingSupportingImages,
    supportingImageError,
    isRefiningWithSupportingPhoto,
    supportingPhotoGuidance,
    photoQuality,
    supportingPhotoQuality,
    photoCap,
    refinementPolicy,
    refinementSummary,
    photoCapGate,
    dismissPhotoCapGate,
    handleRefinementGateShown,
    requestSupportingPhotoSignIn,

    // Mode state
    analysisMode,
    setAnalysisMode,

    // Analysis state
    isAnalyzing,
    analysisResult,
    diagnosisResult,
    savedAnalysisId,
    recoveredWeed,
    isRecoveringWeed,
    weedRecoveryFailed,
    funnelId,
    attemptId,
    originLawnId,
    selectedLawnId,
    limitReached,
    limitTier,
    upgradeRequired,
    analysisNetworkError,
    analysisSaveError,
    analysisServerError,

    // ZIP code state
    zipCode,
    zipCodeError,
    isZipCodeValid,

    // Email gate state
    carePlanEmail,
    carePlanEmailError,
    emailSuggestion,
    isSubmittingCarePlan,
    emailGateCompleted,
    completedTimelineUrl,
    timelineRedirectUrl,
    oauthResumeUrl,
    navigateToCompletedTimeline,

    // UI state
    returnedFromGuide,
    dismissReturnedFromGuide,

    // Auth
    user,
    authLoading,

    // Handlers
    handleImageUpload,
    handleSupportingImagesUpload,
    removeSupportingImage,
    analyzeImage,
    recoverIdentifyAsWeed,
    retryAnalysis,
    resetUpload,
    retryWithNewPhoto,
    refineWithSupportingPhoto,
    handleZipCodeChange,
    handleZipCodeKeyPress,
    handleCarePlanEmailChange,
    handleAcceptEmailSuggestion,
    handleDismissEmailSuggestion,
    handleCarePlanEmailKeyPress,
    handleCarePlanSubmit,
    handleResultPrimaryCtaClick,
    scanUsage,
    anonQuotaReason,
    handleScanQuotaWarningShown,
    handleScanQuotaWarningClicked,
    handleImpermanenceNoteClick,
    handleSaveAndViewTimeline,
    selectAlternative,
    confirmIdentity,
    clearDiagnosisResult,
    clearAnalysisResult,
    persistDiagnosisForOAuth,
    authIsPremium,
    subscriptionLoading,
  } = useHomepageAnalysis({
    lawnContext: analysisLawn.lawnContext,
  })

  // Back-button rescue: surface a recent saved analysis if the user landed
  // back on `/` within 60 minutes of identifying their grass. Banner is
  // hidden while an analysis is in flight or already on screen.
  const { breadcrumb: lastAnalysisBreadcrumb, clear: dismissLastAnalysisBreadcrumb } =
    useLastAnalysisBreadcrumb()
  const showLastAnalysisBanner =
    Boolean(lastAnalysisBreadcrumb) &&
    !analysisResult &&
    !diagnosisResult &&
    !isAnalyzing &&
    !limitReached &&
    !upgradeRequired &&
    !(uploadedImage || uploadedImageUrl)

  // A limit wall has replaced the analyzer, so the page is showing one ask
  // rather than a pitch. Drives what gets stripped above the card.
  const wallShowing = limitReached || upgradeRequired || Boolean(photoCapGate)

  // Hero entrance respects user motion preference. Page-level MotionConfig
  // already short-circuits motion under reduced motion, but the decorative
  // idle float on the care-plan SVG is something we explicitly opt-out of.
  const reduce = useReducedMotion()
  const limitReachedPromptShownRef = useRef(false)
  const upgradeRequiredPromptShownRef = useRef(false)

  useEffect(() => {
    if (!limitReached) {
      limitReachedPromptShownRef.current = false
      return
    }
    if (limitReachedPromptShownRef.current) return

    analytics.upgradePromptShown(
      'usage_limit',
      ANONYMOUS_WEB_ANALYSIS_LIMITS[analysisMode],
      ANONYMOUS_WEB_ANALYSIS_LIMITS[analysisMode],
    )
    limitReachedPromptShownRef.current = true
  }, [analysisMode, limitReached])

  useEffect(() => {
    if (!upgradeRequired) {
      upgradeRequiredPromptShownRef.current = false
      return
    }
    if (upgradeRequiredPromptShownRef.current) return

    analytics.upgradePromptShown(
      'usage_limit',
      REGISTERED_FREE_ANALYSIS_LIMITS[analysisMode],
      REGISTERED_FREE_ANALYSIS_LIMITS[analysisMode],
    )
    upgradeRequiredPromptShownRef.current = true
  }, [analysisMode, upgradeRequired])

  const registeredExtraScans = Math.max(
    0,
    REGISTERED_FREE_ANALYSIS_LIMITS[analysisMode] - ANONYMOUS_WEB_ANALYSIS_LIMITS[analysisMode],
  )

  // Scroll to the active section on transitions. The upload→ZIP step is
  // handled by focus management in ZipCodeStep (browser auto-scrolls focused
  // inputs, with `scroll-mt-24` for the header offset), so this effect only
  // covers the analyzing card and the results section.
  //
  // Dedupes within ~700ms because setAnalysisResult and setIsAnalyzing(false)
  // flip on separate ticks; both runs would otherwise schedule a scroll.
  const lastScrollRef = useRef<{ id: string; at: number } | null>(null)
  const hadResultRef = useRef(false)
  const gateOverResult = Boolean(photoCapGate) && Boolean(analysisResult || diagnosisResult)
  useEffect(() => {
    const hasResult = Boolean(analysisResult || diagnosisResult)
    const cameBackFromResult = !hasResult && !isAnalyzing && hadResultRef.current
    hadResultRef.current = hasResult

    let target: { id: string; mode: 'center' | 'start' } | null = null
    if (gateOverResult) {
      // The refinement gate renders alongside the still-visible result, which
      // can be a full viewport below where the user tapped. 'start' plus the
      // section's scroll-mt-24 pins the card under the header; 'center' on a
      // short card near the top of the document resolves to roughly y=0 and
      // reads as a jump to the top of the page.
      target = { id: 'photo-cap-gate', mode: 'start' }
    } else if (hasResult) {
      target = { id: 'results-section', mode: 'start' }
    } else if (isAnalyzing) {
      target = { id: 'analysis-card', mode: 'center' }
    } else if (cameBackFromResult) {
      // "Try a different photo" remounts the hero, so the uploader lands
      // below the fold on a phone and the user is left hunting for it.
      target = { id: 'analysis-card', mode: 'center' }
    }

    if (!target) return
    const { id, mode } = target

    const last = lastScrollRef.current
    if (last && last.id === id && Date.now() - last.at < 700) return

    // Double rAF: second frame fires after the post-state-change layout has
    // committed, so the element is in its final position when we scroll.
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById(id)
        if (!el) return
        // Skip the scroll on the analyzing card and the gate when either is
        // already fully on-screen — desktop users would otherwise see a tiny
        // snap every time the card morphs in place. The results-section scroll
        // is always run because it's a separate section below the hero and
        // the user genuinely needs to be repositioned to it.
        if (id === 'analysis-card' || id === 'photo-cap-gate') {
          scrollRefinementTargetIntoView(id, mode)
        } else {
          // Header offset is handled by `scroll-mt-24` on the target sections
          // (CSS, not JS measurement) so this branch stays purely declarative.
          el.scrollIntoView({ behavior: 'auto', block: mode })
        }
        lastScrollRef.current = { id, at: Date.now() }
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [isAnalyzing, analysisResult, diagnosisResult, gateOverResult])

  // Recover the hash scroll when the user lands on `/#analysis-card` from a
  // cross-page link. The browser's native anchor-scroll runs against the SSR
  // tree before the Suspense fallback swaps for the live tree, and the
  // fallback may finish a frame too late for the scroll to land. Re-run the
  // scroll once after the live `#analysis-card` is in the DOM. Gated on
  // `scrollY === 0` so we never yank a user who already scrolled.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.hash !== '#analysis-card') return
    if (window.scrollY !== 0) return

    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById('analysis-card')
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.top >= 80 && rect.bottom <= window.innerHeight) {
          lastScrollRef.current = { id: 'analysis-card', at: Date.now() }
          return
        }
        el.scrollIntoView({ behavior: 'auto', block: 'center' })
        lastScrollRef.current = { id: 'analysis-card', at: Date.now() }
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [])

  // Expose in-flight analysis state on the body so the header can detect it
  // and switch from a hard reload to a soft `homeReset` event when the user
  // clicks the logo mid-analysis.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const hasHomeAnalysisState = Boolean(
      isAnalyzing
        || uploadedImage
        || uploadedImageUrl
        || analysisResult
        || diagnosisResult
        || limitReached
        || upgradeRequired
        || photoCapGate
    )
    document.body.dataset.analyzing = isAnalyzing ? 'true' : 'false'
    document.body.dataset.homeAnalysisActive = hasHomeAnalysisState ? 'true' : 'false'
    // Narrower than homeAnalysisActive: true only once a result is on screen.
    // The server-rendered FAQ section in app/page.tsx hides on this flag so
    // the results view stays focused (the old client FAQ unmounted instead).
    document.body.dataset.homeResults = analysisResult || diagnosisResult ? 'true' : 'false'
    return () => {
      document.body.dataset.analyzing = 'false'
      document.body.dataset.homeAnalysisActive = 'false'
      document.body.dataset.homeResults = 'false'
    }
  }, [isAnalyzing, uploadedImage, uploadedImageUrl, analysisResult, diagnosisResult, limitReached, upgradeRequired, photoCapGate])

  // Idle warm for the ZIP step. The upload-triggered prefetch below can still
  // lose the race on a slow connection, because compression finishes long
  // before a cold chunk fetch does. Idle time on the hero costs the user
  // nothing and removes the fallback entirely in the common case.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const warm = () => { void import('@/components/home/zip-code-step') }
    const idle = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    }).requestIdleCallback
    if (typeof idle === 'function') {
      idle(warm, { timeout: 3000 })
      return
    }
    const timer = setTimeout(warm, 1500)
    return () => clearTimeout(timer)
  }, [])

  // Prefetch the analyze + result chunks once the user uploads an image.
  // Image compression + state propagation already takes 1-3s, giving the
  // browser ample time to fetch and parse the chunks before the user clicks
  // Analyze. Without this, AnalysisLoading would briefly render blank because
  // next/dynamic only starts fetching when the component first mounts.
  // import() calls are idempotent — Webpack/Turbopack dedup the chunk request,
  // so re-runs on mode change are safe.
  // ZIP is the step that mounts NEXT, before either loading card, so it is
  // warmed first. Without it the "Loading the location step…" fallback owns the
  // screen for the whole chunk fetch on a cold cache.
  useEffect(() => {
    if (!uploadedImage && !uploadedImageUrl) return
    void import('@/components/home/zip-code-step')
    void import('@/components/home/analysis-loading')
    if (analysisMode === 'diagnose') {
      void import('@/components/home/diagnosis-results')
    } else {
      void import('@/components/home/results-display')
    }
  }, [uploadedImage, uploadedImageUrl, analysisMode])

  // Handle view timeline navigation for authenticated users
  const handleViewTimeline = (location?: unknown) => {
    trackEvent('care_plan_cta_clicked', {
      cta: 'view_timeline',
      location: typeof location === 'string' ? location : undefined,
      entry_path: 'authenticated_result',
      grass_type: analysisResult?.grassType,
      zip_prefix: zipCode ? zipCode.slice(0, 3) : undefined,
      analysis_id: savedAnalysisId ?? undefined,
    })
    trackEvent('care_plan_redirect_started', {
      redirect_reason: 'analysis_already_saved',
      entry_path: 'authenticated_result',
      grass_type: analysisResult?.grassType,
      zip_prefix: zipCode ? zipCode.slice(0, 3) : undefined,
      analysis_id: savedAnalysisId ?? undefined,
    })
  }

  // Handle mode change with URL sync
  const handleModeChange = (mode: AnalysisMode) => {
    setAnalysisMode(mode)
    const url = new URL(window.location.href)
    if (mode === 'diagnose') {
      url.searchParams.set('mode', 'diagnose')
    } else {
      url.searchParams.delete('mode')
    }
    window.history.replaceState({}, '', url.toString())
  }

  return (
    <MotionConfig reducedMotion="user">
    <div className="relative overflow-hidden">
      {/* Aria-live region for dynamic content updates */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="analysis-status"
      >
        {isUploading && "Uploading image..."}
        {isAnalyzing && (analysisMode === 'diagnose' ? "Diagnosing your lawn problem..." : "Analyzing your grass image...")}
        {analysisResult && (
          analysisResult.identitySource === 'user_selected'
            ? `Using the grass you selected: ${analysisResult.grassType}.`
            : `Analysis complete. Your grass type is ${analysisResult.grassType} with ${analysisResult.displayConfidence ?? analysisResult.confidence}% confidence.`
        )}
        {diagnosisResult && (
          diagnosisResult.outcome === 'healthy'
            ? 'Diagnosis complete. The photo shows a healthy lawn.'
            : diagnosisResult.outcome === 'inconclusive'
              ? 'Diagnosis inconclusive. Clearer targeted photos are needed before treatment.'
              : `Diagnosis complete. ${diagnosisResult.problems.length} problem${diagnosisResult.problems.length !== 1 ? 's' : ''} identified.`
        )}
      </div>

      <div>
        {/* Upload Section - shown when no analysis or diagnosis result */}
        {!analysisResult && !diagnosisResult && (
          <HomeHeroShell>
              {/* Back-button rescue banner — surfaces a recent saved analysis
                  on idle homepage visits. Rendered above the hero so it's
                  the first thing the user sees if they bounced off a result
                  page within the TTL window. Auto-hides during analyze
                  and clears on a fresh analysis. */}
              {showLastAnalysisBanner && lastAnalysisBreadcrumb && (
                <LastAnalysisBanner
                  breadcrumb={lastAnalysisBreadcrumb}
                  onDismiss={dismissLastAnalysisBreadcrumb}
                />
              )}
              {/* The pitch retires once a wall replaces the analyzer. The hero
                  sells the scan, and by the time a wall renders the visitor has
                  already bought that pitch — they picked a photo and typed a
                  ZIP. Leaving it up pushed the ask about 500px down the phone
                  viewport, so the one decision on the screen started below the
                  fold while resolved copy held the top.

                  Safe for SEO and for LCP: both states require an analyze round
                  trip to reach, so the SSR HTML and every crawler still get the
                  H1 and the count. */}
              {!wallShowing && (
                <>
                  {/* HeroSection renders outside the motion wrapper so the H1 (the
                      LCP element) paints from the SSR HTML without waiting on
                      framer-motion to hydrate. Only the upload card and mode
                      toggle below get the entrance animation. */}
                  <HeroSection mode={analysisMode} animate />
                  {/* Social proof count row. The old testimonial/star treatment was
                      retired; this variant is count-only and describes the actual
                      cross-mode analysis total. */}
                  <div className="min-h-[44px]">
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...FADE_TRANSITION, delay: 0.04 }}
                    >
                      <HeroSocialProof
                        variant="verified_count_v3"
                        countLabel={socialProofCountLabel}
                      />
                    </motion.div>
                  </div>
                </>
              )}
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...FADE_TRANSITION, delay: 0.1 }}
                className="relative"
              >

              {/* Card + decorative art. The wrapper anchors corner art to
                  the card itself, not the motion container (which also
                  includes the mode toggle below). */}
              <div className="relative">
              <Card id="analysis-card" className="relative z-10 bg-white/95 shadow-[0_12px_40px_rgb(0,0,0,0.04)] border border-white/60 rounded-2xl overflow-hidden">
                {/* The anon limit wall owns its full frame (edge-bleed photo
                    header), so the card's padding drops while it shows.
                    sm:p-0 is required: CardContent's base sm:px-6/sm:pb-6
                    survive a bare p-0 through tailwind-merge. */}
                <CardContent className={limitReached ? "p-0 sm:p-0" : "p-6 md:p-10"}>
                  {!limitReached && !upgradeRequired && (
                    <WizardProgress
                      currentStep={
                        (!(uploadedImage || uploadedImageUrl)
                          ? 1
                          : !analysisResult && !diagnosisResult
                            ? 2
                            : 3) as WizardStep
                      }
                      mode={analysisMode}
                    />
                  )}
                  <AnimatePresence mode="wait" initial={false}>
                  {!(uploadedImage || uploadedImageUrl) ? (
                    // Initial upload state
                    <motion.div
                      key="upload"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                    {!authLoading && !user && (
                      <AnonQuotaNotice
                        mode={analysisMode}
                        reason={anonQuotaReason}
                        extraScans={registeredExtraScans}
                        redirectPath={analysisMode === 'diagnose' ? '/diagnose' : '/'}
                      />
                    )}
                    <UploadCard
                      onUpload={handleImageUpload}
                      isUploading={isUploading}
                      uploadError={uploadError}
                      mode={analysisMode}
                    />
                    </motion.div>
                  ) : (
                    // After image uploaded
                    <motion.div
                      key="post-upload"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="text-center"
                    >
                      {limitReached ? (
                        <AnonLimitWall
                          mode={analysisMode}
                          imageSrc={uploadedImage || uploadedImageUrl}
                          oauthRedirectTo={oauthResumeUrl}
                          extraScans={registeredExtraScans}
                          appLinkPlacement="grass_id_limit_anon"
                          zipCode={zipCode}
                          tier={limitTier}
                        />
                      ) : upgradeRequired ? (
                        <PremiumLimitWall
                          mode={analysisMode}
                          imageSrc={uploadedImage || uploadedImageUrl}
                          appLinkPlacement="grass_id_limit_upgrade"
                        />
                      ) : photoCapGate ? (
                        // Photo-count gate, not a quota wall. It sits in the
                        // same slot so the ask lands where the analyzer was.
                        <PhotoCapGate
                          mode={photoCapGate.mode}
                          intent={photoCapGate.intent}
                          trigger={photoCapGate.reason}
                          requiredTier={photoCapGate.requiredTier}
                          cap={photoCapGate.cap}
                          requested={photoCapGate.requested}
                          denialReason={photoCapGate.denialReason ?? null}
                          guidance={refinementPolicy?.nextPhotoGuidance ?? supportingPhotoGuidance}
                          imageSrc={uploadedImage || uploadedImageUrl}
                          oauthRedirectTo={oauthResumeUrl}
                          onDismiss={dismissPhotoCapGate}
                        />
                      ) : !isAnalyzing ? (
                        // ZIP code input step. Retryable network, server, and save
                        // failures keep the photo + ZIP in place.
                        <>
                          {(analysisNetworkError || analysisSaveError || analysisServerError) && (
                            <div
                              role="alert"
                              className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left"
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                  {analysisSaveError || analysisServerError ? (
                                    <RefreshCw className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                                  ) : (
                                    <WifiOff className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-amber-900">
                                    {analysisSaveError
                                      ? "Your result wasn't saved"
                                      : analysisServerError
                                        ? "The analysis didn't finish"
                                        : "Your connection dropped"}
                                  </p>
                                  <p className="mt-0.5 text-[13px] text-amber-800/90 leading-snug">
                                    {analysisSaveError
                                      ? "The analysis finished, but we could not save the result. Your photo is still here, and this scan was not counted."
                                      : analysisServerError
                                        ? "Your photo is still here, so you can try again."
                                        : "We didn't finish analyzing your photo. Your photo is still here, so you can pick up right where you left off."}
                                  </p>
                                  <Button
                                    onClick={retryAnalysis}
                                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white min-h-[44px] font-semibold"
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                                    Try again
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                          <ZipCodeStep
                            zipCode={zipCode}
                            zipCodeError={zipCodeError}
                            isZipCodeValid={isZipCodeValid}
                            uploadedImage={uploadedImage || uploadedImageUrl || ''}
                            onZipCodeChange={handleZipCodeChange}
                            onZipCodeKeyPress={handleZipCodeKeyPress}
                            onAnalyze={analyzeImage}
                            onReset={resetUpload}
                            mode={analysisMode}
                            supportingImages={supportingImages}
                            isUploadingSupportingImages={isUploadingSupportingImages}
                            supportingImageError={supportingImageError}
                            onSupportingImagesUpload={handleSupportingImagesUpload}
                            onRemoveSupportingImage={removeSupportingImage}
                            isRefinementStep={isRefiningWithSupportingPhoto}
                            requireSupportingPhoto={isRefiningWithSupportingPhoto}
                            supportingPhotoGuidance={supportingPhotoGuidance}
                            supportingPhotosLocked={!user}
                            onSupportingPhotosSignIn={requestSupportingPhotoSignIn}
                            photoCap={photoCap}
                            lawns={lawns}
                            lawnChoice={analysisLawn.choice ?? null}
                            lawnChoiceRequired={analysisLawn.choiceRequired}
                            lawnChoiceMade={analysisLawn.choiceMade}
                            onLawnChoice={analysisLawn.selectLawn}
                            funnelId={funnelId}
                            attemptId={attemptId}
                            authState={user ? 'authenticated' : 'anonymous'}
                          />
                        </>
                      ) : (
                        // Loading state with mode-aware messages
                        <AnalysisLoading zipCode={zipCode} imageUrl={uploadedImage || uploadedImageUrl || undefined} mode={analysisMode} funnelId={funnelId} attemptId={attemptId} authState={user ? 'authenticated' : 'anonymous'} />
                      )}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </CardContent>
              </Card>

              {/* Left-side grass — woven through the upload card the same
                  way the care-plan card threads its blades. Back layer sits
                  behind the card (z-0) and front layer in front (z-20),
                  so the panel reads as growing up out of the lawn. No
                  background glow — matches the care-plan card's clean
                  blade-only treatment. Fades in after the card so the
                  decorative art reads as "growing" rather than popping. */}
              {!(uploadedImage || uploadedImageUrl) && !limitReached && !upgradeRequired && (
                <>
                  <motion.div
                    aria-hidden="true"
                    className="hidden md:block absolute pointer-events-none"
                    style={{
                      bottom: '-4rem',
                      left: '-3rem',
                      width: '14rem',
                      zIndex: 0,
                    }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.32 }}
                  >
                    <HeroGrassThrough side="left" layer="back" />
                  </motion.div>
                  <motion.div
                    aria-hidden="true"
                    className="hidden md:block absolute pointer-events-none"
                    style={{
                      bottom: '-4rem',
                      left: '-3rem',
                      width: '14rem',
                      zIndex: 20,
                    }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.36 }}
                  >
                    <HeroGrassThrough side="left" layer="front" />
                  </motion.div>
                </>
              )}

              {/* Care-plan preview — overlapping the upload card so the eye
                  travels photo → plan. Entrance: starts further out and
                  more tilted, then settles to its 8deg resting angle. An
                  inner motion node carries a slow yoyo float so the card
                  feels alive without ever taking visual focus from the
                  primary CTA. */}
              {!(uploadedImage || uploadedImageUrl) && !limitReached && !upgradeRequired && (
                <motion.div
                  aria-hidden="true"
                  className="hidden md:block absolute pointer-events-none z-30 drop-shadow-[0_24px_36px_rgba(22,101,52,0.16)] md:-bottom-20 md:right-0 md:w-64 lg:-bottom-28 lg:-right-[7.5rem] lg:w-80 xl:-right-[10.5rem] xl:w-[22rem]"
                  initial={reduce ? { rotate: 8 } : { opacity: 0, y: 24, rotate: 14 }}
                  animate={reduce ? { rotate: 8 } : { opacity: 1, y: 0, rotate: 8 }}
                  transition={{ duration: 0.6, ease: EASE_OUT_EXPO, delay: 0.28 }}
                >
                  {/* Settles to its 8deg resting angle on entrance and stays
                      put — no perpetual idle float, so the hero has no
                      always-moving element competing with the CTA. */}
                  <CarePlanCardSvg />
                </motion.div>
              )}
              </div>

              {/* Secondary affordance — demoted from a peer toggle so identification
                  stays the dominant CTA. Direction flips with the current mode. */}
              {!(uploadedImage || uploadedImageUrl) && (
                <motion.div
                  className="text-center mt-5"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...FADE_TRANSITION, delay: 0.2 }}
                >
                  {analysisMode === 'identify' ? (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                    <Link
                      href="/diagnose"
                      onClick={(event) => {
                        if (isUploading || isAnalyzing) {
                          event.preventDefault()
                          return
                        }
                        trackEvent('cta_clicked', {
                          cta_location: 'homepage_mode_toggle',
                          cta_text: 'Diagnose a lawn problem instead',
                          target_mode: 'diagnose',
                        })
                        // Navigate to the dedicated /diagnose page rather than
                        // swapping mode in place. The in-place swap changed the
                        // above-the-fold hero while this button sits below the
                        // CTA, so the click produced no visible feedback at the
                        // user's scroll position. /diagnose is a full first-class
                        // flow, and this mirrors how /diagnose's reverse switch
                        // ("Identify my grass instead") already navigates to /.
                      }}
                      aria-disabled={isUploading || isAnalyzing}
                      className="inline-flex items-center gap-2 min-h-[44px] px-5 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Stethoscope className="w-4 h-4 text-green-600" aria-hidden="true" />
                      Diagnose a lawn problem instead
                    </Link>
                    {/* Wrong-subject deflection chip REMOVED 2026-08-22 (Anthony):
                        pre-scan exits bypass the failed-result page, which is
                        now a refinement sign-up surface. The identify-to-weed
                        reroute AFTER the result remains the recovery path.
                        Component kept for a possible post-result placement. */}
                    </div>
                  ) : (
                    <motion.button
                      type="button"
                      onClick={() => {
                        trackEvent('cta_clicked', {
                          cta_location: 'homepage_mode_toggle',
                          cta_text: 'Identify my grass instead',
                          target_mode: 'identify',
                        })
                        handleModeChange('identify')
                      }}
                      disabled={isUploading || isAnalyzing}
                      whileTap={{ scale: 0.97 }}
                      transition={{ duration: 0.12 }}
                      className="inline-flex items-center gap-2 min-h-[44px] px-5 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Leaf className="w-4 h-4 text-green-600" aria-hidden="true" />
                      Identify my grass instead
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Mobile-only mini preview — desktop gets the peeking SVG;
                  mobile needs an inline hint so users see "what comes back"
                  before scrolling away from the upload card. Grass cluster
                  grounds the card at the bottom so it reads as the same
                  visual language as the desktop hero, scaled to portrait. */}
              {!(uploadedImage || uploadedImageUrl) && !limitReached && !upgradeRequired && (
                <motion.div
                  className="md:hidden mt-16"
                  variants={{
                    hidden: {},
                    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
                  }}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                >
                  <motion.h2
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
                    }}
                    className="text-center font-bold tracking-[-0.02em] text-gray-900 mb-6 text-[28px] md:text-4xl"
                  >
                    What you&apos;ll get
                  </motion.h2>
                  <motion.ul
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
                    }}
                    className="flex flex-col gap-2 max-w-[300px] mx-auto mb-8 text-sm text-gray-700"
                  >
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Species name with confidence score
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      12-month care plan
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Timing tuned to your zip
                    </li>
                  </motion.ul>
                  <motion.div
                    aria-hidden="true"
                    className="relative w-[calc(100vw-4rem)] max-w-[400px] mx-auto"
                    variants={{
                      hidden: { opacity: 0, y: 12 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
                    }}
                  >
                    <div
                      className="relative z-10"
                      style={{
                        transform: 'rotate(2deg)',
                        filter: 'drop-shadow(0 16px 24px rgba(22, 101, 52, 0.14))',
                      }}
                    >
                      <CarePlanCardSvg compact />
                    </div>
                  </motion.div>
                </motion.div>
              )}
              </motion.div>

          </HomeHeroShell>
        )}

        {/* Below-the-fold marketing sections — shown alongside the upload state, hidden once
            results render. This client subtree suspends on useSearchParams, so its DOM is NOT in
            the statically-prerendered HTML (Googlebot sees it only after rendering JS). The
            SEO-critical JSON-LD (FAQPage + HowTo) is therefore emitted server-side in app/page.tsx,
            OUTSIDE the Suspense boundary, so it always lands in the static HTML. HowItWorksIllustrated
            is still passed as the aboveCalculatorsSlot for its in-flow visual placement. */}
        {!analysisResult && !diagnosisResult && (
          <>
            {aboveCalculatorsSlot}
            <MidPageCta />
            <ToolsChipStrip />
            <HomeAppSection />
            {/* Once the iPhone app is live, the app section owns this slot and
                the "Simple pricing" preview comes off the homepage — the
                pricing story lives on /pricing and inside the app funnel.
                Pre-launch builds (no promo env vars) keep the pricing preview
                since HomeAppSection self-hides there. */}
            {/* The FAQ moved to a server-rendered <details> section in
                app/page.tsx so answers land in the static HTML. */}
            {!isAppPromoLive && <PricingPreview />}
            <FinalCta />
          </>
        )}

        {/* Refinement sign-in gate for an anonymous visitor who tapped a
            refinement CTA. The result deliberately stays on screen there, so
            the gate cannot live in the analyzer card above — that whole
            subtree only renders while there is no result yet. */}
        {gateOverResult && photoCapGate && (
          <section id="photo-cap-gate" className="scroll-mt-24 mx-auto mb-8 w-full max-w-2xl px-4">
            <Card className="relative z-10 bg-white/95 shadow-[0_12px_40px_rgb(0,0,0,0.04)] border border-white/60 rounded-2xl overflow-hidden">
              <CardContent className="p-6 md:p-10">
                <PhotoCapGate
                  mode={photoCapGate.mode}
                  intent={photoCapGate.intent}
                  trigger={photoCapGate.reason}
                  requiredTier={photoCapGate.requiredTier}
                  cap={photoCapGate.cap}
                  requested={photoCapGate.requested}
                  denialReason={photoCapGate.denialReason ?? null}
                  guidance={refinementPolicy?.nextPhotoGuidance ?? supportingPhotoGuidance}
                  imageSrc={uploadedImage || uploadedImageUrl}
                  oauthRedirectTo={oauthResumeUrl}
                  onDismiss={dismissPhotoCapGate}
                />
              </CardContent>
            </Card>
          </section>
        )}

        {/* Diagnosis Results Section */}
        {diagnosisResult && (
          <DiagnosisResults
            subscriptionLoading={subscriptionLoading}
            isPremium={authIsPremium || Boolean(scanUsage?.isPremium)}
            result={diagnosisResult}
            uploadedImage={uploadedImage || ''}
            zipCode={zipCode}
            savedAnalysisId={savedAnalysisId}
            funnelId={funnelId}
            attemptId={attemptId}
            originLawnId={originLawnId}
            selectedLawnId={selectedLawnId}
            zipMatch={analysisLawn.selectedLawn?.zip_code
              ? analysisLawn.selectedLawn.zip_code === zipCode
              : null}
            onReset={retryWithNewPhoto}
            onRefineWithSupportingPhoto={refineWithSupportingPhoto}
            refinementLocked={!user}
            refinementPending={Boolean(photoCapGate)}
            refinementPolicy={refinementPolicy}
            onRefinementGateShown={handleRefinementGateShown}
            onSwitchToIdentify={() => {
              handleModeChange('identify')
              clearDiagnosisResult()
            }}
            user={user}
            authLoading={authLoading}
            redirectUrl={oauthResumeUrl}
            onBeforeOAuth={persistDiagnosisForOAuth}
            carePlanEmail={carePlanEmail}
            carePlanEmailError={carePlanEmailError}
            emailSuggestion={emailSuggestion}
            isSubmittingCarePlan={isSubmittingCarePlan}
            emailGateCompleted={emailGateCompleted}
            timelineRedirectUrl={timelineRedirectUrl}
            onEmailChange={handleCarePlanEmailChange}
            onEmailKeyPress={handleCarePlanEmailKeyPress}
            onEmailSubmit={handleCarePlanSubmit}
            onPrimaryCtaClick={withResultCtaLocation("home_diagnose_result", handleResultPrimaryCtaClick)}
            onAcceptEmailSuggestion={handleAcceptEmailSuggestion}
            onDismissEmailSuggestion={handleDismissEmailSuggestion}
            onSeeCarePlan={navigateToCompletedTimeline}
            onSaveAndViewTimeline={handleSaveAndViewTimeline}
            onViewTimeline={handleViewTimeline}
          />
        )}

        {recoveredWeed && (
          <WeedResultPresentation
            result={recoveredWeed.result}
            analysisId={recoveredWeed.analysisId}
            accessToken={recoveredWeed.resultAccessToken}
            funnelId={funnelId ?? `web_${recoveredWeed.analysisId}`}
            attemptId={recoveredWeed.attemptId}
            entry="identify_recovery"
            zipCode={zipCode}
          />
        )}

        {/* Analysis Results Section (identify mode) */}
        {analysisResult && !recoveredWeed && (
          <ResultsDisplay
            authIsPremium={authIsPremium}
            subscriptionLoading={subscriptionLoading}
            analysisResult={analysisResult}
            uploadedImage={uploadedImage || ''}
            zipCode={zipCode}
            savedAnalysisId={savedAnalysisId}
            funnelId={funnelId}
            attemptId={attemptId}
            user={user}
            carePlanEmail={carePlanEmail}
            emailSuggestion={emailSuggestion}
            isSubmittingCarePlan={isSubmittingCarePlan}
            emailGateCompleted={emailGateCompleted}
            onSeeCarePlan={navigateToCompletedTimeline}
            carePlanHref={completedTimelineUrl ?? undefined}
            timelineRedirectUrl={timelineRedirectUrl}
            onReset={retryWithNewPhoto}
            onRefineWithSupportingPhoto={refineWithSupportingPhoto}
            refinementLocked={!user}
            refinementPending={Boolean(photoCapGate)}
            refinementPolicy={refinementPolicy}
            refinementSummary={refinementSummary}
            onRefinementGateShown={handleRefinementGateShown}
            onEmailChange={handleCarePlanEmailChange}
            onEmailKeyPress={handleCarePlanEmailKeyPress}
            onEmailSubmit={handleCarePlanSubmit}
            onPrimaryCtaClick={withResultCtaLocation("home_identify_result", handleResultPrimaryCtaClick)}
            scanUsage={scanUsage}
            onQuotaWarningShown={handleScanQuotaWarningShown}
            onQuotaWarningClicked={handleScanQuotaWarningClicked}
            showImpermanenceNote
            onImpermanenceNoteClick={handleImpermanenceNoteClick}
            onAcceptEmailSuggestion={handleAcceptEmailSuggestion}
            onDismissEmailSuggestion={handleDismissEmailSuggestion}
            onSaveAndViewTimeline={handleSaveAndViewTimeline}
            onViewTimeline={handleViewTimeline}
            onSelectAlternative={selectAlternative}
            onConfirmIdentity={confirmIdentity}
            returnedFromGuide={returnedFromGuide}
            onDismissReturnedFromGuide={dismissReturnedFromGuide}
            onDiagnoseThisPhoto={() => {
              handleModeChange('diagnose')
              clearAnalysisResult()
              // Pass the override explicitly because this callback still holds
              // the pre-setState identify mode in the current tick.
              // Do not reuse an abstained Identify result (normally
              // Unknown/Other for this CTA) as host-grass context. The hook
              // still supplies a saved-lawn context when one actually exists.
              void analyzeImage('diagnose')
            }}
            isWeedRecoveryAvailable={weedCapability.recoveryEnabled}
            isWeedRecoveryLoading={isRecoveringWeed}
            weedRecoveryFunnelId={funnelId}
            weedRecoveryCohort={weedCapability.cohort}
            weedRecoveryFailed={weedRecoveryFailed}
            onIdentifyWeedThisPhoto={weedCapability.recoveryEnabled
              ? () => { void recoverIdentifyAsWeed() }
              : undefined}
          />
        )}
      </div>

    </div>
    </MotionConfig>
  )
}
